import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Nav, Alert, Badge } from "react-bootstrap";
import { FaFont, FaFile, FaPlus, FaRobot, FaMagic } from "react-icons/fa";
import { Icon } from "../../../../../components/common/Icon";
import AiAssistantSourceForm from "./AiAssistantSourceForm";

interface AddSourceModalProps {
    show: boolean;
    onHide: () => void;
    chatbotId?: number;
    onSubmit: (data: {
        type: "text" | "file";
        title: string;
        lang: string;
        text?: string;
        file?: File;
    }) => Promise<void>;
    isSubmitting: boolean;
}

const AddSourceModal: React.FC<AddSourceModalProps> = ({
    show,
    onHide,
    onSubmit,
    isSubmitting,
    chatbotId,
}) => {
    const [mode, setMode] = useState<"standard" | "ai">("standard");
    const [activeTab, setActiveTab] = useState<"text" | "file">("text");
    const [title, setTitle] = useState("");
    const [lang, setLang] = useState("en");
    const [text, setText] = useState("");
    const [file, setFile] = useState<File | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Reset form when modal opens
    useEffect(() => {
        if (show) {
            setTitle("");
            setLang("en");
            setText("");
            setFile(null);
            setError(null);
            setActiveTab("text");
            setMode("standard");
        }
    }, [show]);

    const handleSubmit = async () => {
        setError(null);

        if (!title.trim()) {
            setError("Please enter a title for the source.");
            return;
        }

        if (activeTab === "text" && !text.trim()) {
            setError("Please enter the text content.");
            return;
        }

        if (activeTab === "file" && !file) {
            setError("Please select a file to upload.");
            return;
        }

        try {
            await onSubmit({
                type: activeTab,
                title,
                lang,
                text: activeTab === "text" ? text : undefined,
                file: activeTab === "file" ? file! : undefined,
            });
            onHide();
        } catch (err: any) {
            setError(err.message || "Failed to create source");
        }
    };

    const languages = [
        { code: "en", name: "English", flag: "🇬🇧" },
        { code: "tr", name: "Türkçe", flag: "🇹🇷" },
        { code: "de", name: "Deutsch", flag: "🇩🇪" },
        { code: "fr", name: "Français", flag: "🇫🇷" },
        { code: "es", name: "Español", flag: "🇪🇸" },
        { code: "it", name: "Italiano", flag: "🇮🇹" },
        { code: "pt", name: "Português", flag: "🇵🇹" },
        { code: "ru", name: "Русский", flag: "🇷🇺" },
        { code: "ar", name: "العربية", flag: "🇸🇦" },
        { code: "ja", name: "日本語", flag: "🇯🇵" },
        { code: "ko", name: "한국어", flag: "🇰🇷" },
        { code: "zh", name: "中文", flag: "🇨🇳" },
    ];

    const handleAiGenerate = (content: string, generatedTitle: string = "", generatedLang: string = "") => {
        setText(content);
        if (generatedTitle) setTitle(generatedTitle);
        if (generatedLang) setLang(generatedLang);
        setMode("standard");
        setActiveTab("text");
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center gap-2">
                    Add Knowledge Source
                    {mode === "ai" && <Badge bg="info" className="ms-2"><Icon icon={FaRobot} className="me-1" /> AI Assistant</Badge>}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* Unified Tabs */}
                <Nav variant="tabs" className="mb-4">
                    <Nav.Item>
                        <Nav.Link
                            id="text_tab"
                            active={mode === "standard" && activeTab === "text"}
                            onClick={() => { setMode("standard"); setActiveTab("text"); }}
                            className="d-flex align-items-center gap-2"
                        >
                            <Icon icon={FaFont} /> Text Content
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link
                            id="file_tab"
                            active={mode === "standard" && activeTab === "file"}
                            onClick={() => { setMode("standard"); setActiveTab("file"); }}
                            className="d-flex align-items-center gap-2"
                        >
                            <Icon icon={FaFile} /> File Upload
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link
                            id="ai_tab"
                            active={mode === "ai"}
                            onClick={() => setMode("ai")}
                            className="d-flex align-items-center gap-2 text-primary fw-bold"
                            style={mode === "ai" ? { background: 'linear-gradient(to bottom, #f8f9fa, #ffffff)' } : {}}
                        >
                            <Icon icon={FaMagic} /> Create with AI Assistant
                        </Nav.Link>
                    </Nav.Item>
                </Nav>

                {error && <Alert variant="danger">{error}</Alert>}

                {mode === "ai" && chatbotId ? (
                    <div className="fade-in-up">
                        <AiAssistantSourceForm
                            chatbotId={chatbotId}
                            initialTitle={title}
                            initialLang={lang}
                            onGenerate={handleAiGenerate}
                        />
                    </div>
                ) : (
                    <Form>
                        <div className="row g-3">
                            <div className="col-md-8">
                                <Form.Group>
                                    <Form.Label>Source Title</Form.Label>
                                    <Form.Control
                                        type="text"
                                        id="modal_source_title"
                                        name="source_title"
                                        placeholder="e.g. Company Policy, Product Manual"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        autoFocus={mode === "standard"}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-4">
                                <Form.Group>
                                    <Form.Label>Language</Form.Label>
                                    <Form.Select
                                        id="modal_source_lang"
                                        name="source_lang"
                                        value={lang}
                                        onChange={(e) => setLang(e.target.value)}
                                    >
                                        {languages.map((l) => (
                                            <option key={l.code} value={l.code}>
                                                {l.flag} {l.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </div>

                            <div className="col-12">
                                {activeTab === "text" ? (
                                    <Form.Group>
                                        <Form.Label>Content</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            id="modal_source_content"
                                            name="source_content"
                                            rows={10}
                                            placeholder="Paste your text content here..."
                                            value={text}
                                            onChange={(e) => setText(e.target.value)}
                                        />
                                        <Form.Text className="text-muted">
                                            The text will be chunked and indexed automatically.
                                        </Form.Text>
                                    </Form.Group>
                                ) : (
                                    <Form.Group>
                                        <Form.Label>Upload File</Form.Label>
                                        <div id="modal_file_dropzone" className="border rounded p-4 text-center bg-light">
                                            <input
                                                type="file"
                                                id="file-upload"
                                                className="d-none"
                                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                                                accept=".pdf,.txt,.md,.json"
                                            />
                                            <label htmlFor="file-upload" className="btn btn-outline-primary mb-2">
                                                Choose File
                                            </label>
                                            <div>
                                                {file ? (
                                                    <span className="fw-bold text-success">{file.name}</span>
                                                ) : (
                                                    <span className="text-muted">Supported: PDF, TXT, MD, JSON</span>
                                                )}
                                            </div>
                                        </div>
                                    </Form.Group>
                                )}
                            </div>
                        </div>
                    </Form>
                )}
            </Modal.Body>
            {mode === "standard" && (
                <Modal.Footer className="d-flex justify-content-between">
                    <Button variant="outline-secondary" onClick={onHide} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        id="add_ingest_btn"
                        variant="premium-ai" /* Using the new premium style */
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="px-4"
                    >
                        {isSubmitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Processing...
                            </>
                        ) : (
                            <>
                                <Icon icon={FaPlus} className="me-2" />
                                Add & Ingest
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            )}
        </Modal>
    );
};

export default AddSourceModal;
